import { IdUsuario } from "../common-types";
import { GenericServiceBase } from "../generic-service";

// DTO para criar chat
export type CreateChatInterno = {
  idUsuario: string;
};

export type ListarChatsInternosQuery = {
  pagina?: number;
  resultadosPorPagina?: number;
};

// DTO para enviar mensagem
export type CreateMensagemInterna = {
  tipo: "texto" | "imagem" | "video" | "audio" | "documento" | "file" | "figurinha";
  mensagem?: string;
  url?: string;
  respostaPara?: string;
  nomeArquivo?: string;
  tamanhoArquivo?: number|string;
  mime?: string;
  legenda?: string;
};

// List item retornado pela listagem de chats internos
export type ChatInternoListItem = {
  id: string;
  idConta: string;
  idUsuarioA: string;
  idUsuarioB: string;
  dataUltimaMensagem?: string | null;
  ultimaMensagemConteudo?: string | null;
  ultimaMensagemEnviadorId?: string | null;
  ultimaMensagemTipo?: string | null;
  usuarioA?: { id: string; nome: string; email: string; foto?: string } | null;
  usuarioB?: { id: string; nome: string; email: string; foto?: string } | null;
  estados?: {
    idUsuario: IdUsuario;
    ultimaLeitura: string | null;
    ultimaEntrega?: string | null;
  }[];
  unreadCount?: number;
};

export type ListarChatsInternosResponse = {
  dados: ChatInternoListItem[];
  pagina: number;
  total: number;
};

// Entidade: Usuário (Simplificado para o chat)
export type ChatUser = {
  id: string;
  nome: string;
  avatar?: string;
};

// Entidade: Chat
export type ChatInterno = {
  id: string;
  nome?: string;
  participantes: ChatUser[];
  ultimaMensagem?: MensagemInterna;
  naoLidas: number;
  createdAt: string;
  estados?: {
    idUsuario: IdUsuario;
    ultimaLeitura: string | null;
    ultimaEntrega?: string | null;
  }[];
  idUsuarioA: IdUsuario
  idUsuarioB: IdUsuario
  usuarioA: { id: IdUsuario, nome: string, email: string, foto: string|null }
  usuarioB: { id: IdUsuario, nome: string, email: string, foto: string|null }
};

export type ChatInternoSocketPayload = {
  id: string;
  idConta: string;
  idUsuarioA: string;
  idUsuarioB: string;
  usuarioA: ChatUser;
  usuarioB: ChatUser;
  dataUltimaMensagem: string | null;
  ultimaMensagemConteudo: string | null;
  ultimaMensagemTipo: string | null;
  ultimaMensagemEnviadorId: string | null;
  estados?: {
    idUsuario: IdUsuario;
    ultimaLeitura: string | null;
    ultimaEntrega?: string | null;
  }[];
};

// Entidade: Mensagem
export type MensagemInterna = {
  id: string;
  idChat: string;
  idRemetente: string;
  tipo:
    | "text"
    | "sticker"
    | "list"
    | "audio"
    | "system"
    | "poll"
    | "file"
    | "contact"
    | "documento"
    | "imagem"
    | "video";

  mensagem: string | null;
  url?: string;
  nomeArquivo?: string;
  tamanhoArquivo?: number | null;
  mime?: string | null;
  legenda?: string | null;
  editado?: boolean | null;

  dataEnvio: string;
  cursor?: string;

  dadosResposta?: {
    id: string;
    conteudo: string;
    idRemetente: string;
    tipoRemetente: string;
    tipo?: string;
    url?: string;
  };
  replyTo?: MensagemInterna;

  reacoes?: Record<string, string> | null;

  // 👇 STATUS LOCAL (derivado, não persistido)
  lida?: boolean;
};

export type BuscarMensagensResponse = {
  mensagens: MensagemInterna[];
  estadoOutroUsuario: {
    ultimaLeitura: string | null;
    ultimaEntrega: string | null;
  };
};

// Params de paginação
export type MensagemQueryParams = {
  cursor?: string;
  quantidade?: number;
};

export type EditarMensagemChatInterno = {
  conteudo?: string;
};

// === CLASSE SERVICE ===

export interface UsuarioConta {
  id: string;
  nome: string;
  email: string;
  foto?: string | null;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  ativo: boolean;
  dataCriacao: string;
}

export class ChatsInternosService extends GenericServiceBase {
  constructor() {
    super("");
  }

  async listarChats(
    idConta: string,
    params?: ListarChatsInternosQuery,
  ): Promise<ListarChatsInternosResponse> {
    const qp: ListarChatsInternosQuery = {
      pagina: 1,
      resultadosPorPagina: 50,
      ...params,
    };
    return (await this.f.get(
      `/contas/${idConta}/chats-internos`,
      qp,
    )) as ListarChatsInternosResponse;
  }

  async criarChat(
    idConta: string,
    dados: CreateChatInterno,
  ): Promise<ChatInterno> {
    return (await this.f.post(
      `/contas/${idConta}/chats-internos`,
      dados,
    )) as ChatInterno;
  }

  async listarMensagens(
    idConta: string,
    idChat: string,
    params: MensagemQueryParams = {},
  ): Promise<BuscarMensagensResponse> {
    return (await this.f.get(
      `/contas/${idConta}/chats-internos/${idChat}/mensagens`,
      params,
    )) as BuscarMensagensResponse;
  }

  async enviarMensagem(
    idConta: string,
    idChat: string,
    dados: CreateMensagemInterna,
  ): Promise<MensagemInterna> {
    return (await this.f.post(
      `/contas/${idConta}/chats-internos/${idChat}/mensagens`,
      dados,
    )) as MensagemInterna;
  }

  async editarMensagem(
    idConta: string,
    idChat: string,
    idMensagem: string,
    dados: EditarMensagemChatInterno,
  ): Promise<void> {
    await this.f.patch(
      `/contas/${idConta}/chats-internos/${idChat}/mensagens/${idMensagem}`,
      dados,
    );
  }

  async marcarComoLido(idConta: string, idChat: string): Promise<void> {
    await this.f.patch(`/contas/${idConta}/chats-internos/${idChat}/lido`);
  }

  async marcarComoEntregue(idConta: string, idChat: string): Promise<void> {
    await this.f.patch(`/contas/${idConta}/chats-internos/${idChat}/entregue`);
  }

  async usuariosContas(idConta: string): Promise<UsuarioConta[]> {
    return (await this.f.get(`/contas/${idConta}/usuarios`)) as UsuarioConta[];
  }

  async usuariosStatus(
    contaId: string,
  ): Promise<{ id: string; isOnline: boolean }[]> {
    return (await this.f.get(`/contas/${contaId}/usuarios/status`)) as {
      id: string;
      isOnline: boolean;
    }[];
  }

  async enviarReacao(
    idConta: string,
    idChat: string,
    idMensagem: string,
    reacao: string,
  ): Promise<void> {
    await this.f.post(
      `/contas/${idConta}/chats-internos/${idChat}/mensagens/${idMensagem}/reacao`,
      { reacao },
    );
  }

}
